/**
 * Logo — the Builder brand lockup (mark + wordmark).
 *
 * April 2026 redesign: JP shipped a custom LogoBuilder.PNG that carries
 * the real brand identity (warm cream ground, ember roof, wordmark
 * locked-up). We route that PNG through this component so every auth,
 * hero, and home surface gets the same mark without each page trying to
 * paint it inline.
 *
 * The PNG already contains its own wordmark, so on the `hero` variant we
 * render the raster-only lockup — no additional text. The `bar` variant
 * keeps the SVG mark (<BuilderMark />) + inline wordmark because the PNG
 * is visually dense at 20px and the SVG reads crisper at that size.
 *
 * Variants:
 *   - 'bar'  — TopBar / nav context. 20px SVG mark + 15px wordmark, horizontal.
 *   - 'hero' — Auth splash, landing. 96px PNG lockup, centered.
 *   - 'mark' — Icon only (PNG, 40px) for use inside cards or drawer headers.
 *
 * If `href` is passed the whole lockup is a Link and gets the site-wide
 * `pressable` feedback (subtle scale on :active). This is not something
 * users will "feel" once, but they tap it hundreds of times as the home
 * button — the missing pressable feedback is exactly the kind of detail
 * that compounds.
 */
import { Link } from 'react-router-dom';
import { BuilderMark } from '@/components/BuilderMark';
import { cn } from '@/lib/cn';

// Served from /public — Vite copies it into the bundle verbatim.
const LOGO_SRC = '/builder.png';

interface LogoProps {
  variant?: 'bar' | 'hero' | 'mark';
  /** Optional href — if set, the lockup is rendered as a Link. */
  href?: string;
  /** Wordmark text. Defaults to "Builder". */
  label?: string;
  className?: string;
}

export function Logo({
  variant = 'bar',
  href,
  label = 'Builder',
  className,
}: LogoProps) {
  const content = (() => {
    if (variant === 'bar') {
      return (
        <span
          className={cn(
            'inline-flex items-center gap-2 text-ink',
            className,
          )}
        >
          <BuilderMark
            className="h-5 w-5 shrink-0 text-ink"
            title={label}
          />
          <span className="text-[15px] font-semibold tracking-tight leading-none">
            {label}
          </span>
        </span>
      );
    }
    if (variant === 'mark') {
      return (
        <img
          src={LOGO_SRC}
          alt={label}
          draggable={false}
          className={cn(
            'h-10 w-10 select-none object-contain',
            'drop-shadow-[0_1px_2px_rgba(15,18,22,0.08)] dark:drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]',
            className,
          )}
        />
      );
    }
    // hero: render the raster lockup at a generous size. The PNG already
    // carries its own wordmark so we do not print the label in text — that
    // would double up. On dark mode we lift the image slightly so the
    // cream background blends with surface-2 instead of punching a hole.
    return (
      <img
        src={LOGO_SRC}
        alt={label}
        draggable={false}
        className={cn(
          'block h-24 w-auto select-none object-contain',
          'drop-shadow-[0_6px_24px_rgba(15,18,22,0.12)]',
          'dark:drop-shadow-[0_10px_40px_rgba(255,170,72,0.25)]',
          className,
        )}
      />
    );
  })();

  if (href) {
    return (
      <Link
        to={href}
        aria-label={label}
        className={cn(
          'pressable inline-flex min-h-11 items-center rounded-xl',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60',
        )}
      >
        {content}
      </Link>
    );
  }
  return content;
}
